// Query hooks — thin wrappers over `db.js` that feed TanStack Query.
// Optimistic updates: every mutation writes to the cache first, then the
// network call, with rollback on error.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { db } from './db.js';
import { todayIsoKey } from './dateKeys.js';

// ───────────── HABITS ─────────────
export function useHabits() {
  return useQuery({ queryKey: ['habits'], queryFn: () => db.listHabits() });
}

export function useCreateHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => db.createHabit(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: ['habits'] });
      const prev = qc.getQueryData(['habits']) || [];
      const optimistic = {
        id: input.id || `tmp-${Date.now()}`,
        name: input.name, icon: input.icon, accent: input.accent,
        frequency: input.frequency || 'Daily', customDays: input.customDays,
        sortOrder: prev.length, streak: 0, longest: 0,
      };
      qc.setQueryData(['habits'], [...prev, optimistic]);
      return { prev };
    },
    onError: (_e, _i, ctx) => qc.setQueryData(['habits'], ctx?.prev || []),
    onSettled: () => qc.invalidateQueries({ queryKey: ['habits'] }),
  });
}

export function useUpdateHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => db.updateHabit(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['habits'] });
      const prev = qc.getQueryData(['habits']) || [];
      qc.setQueryData(['habits'], prev.map(h => h.id === id ? { ...h, ...patch } : h));
      return { prev };
    },
    onError: (_e, _v, ctx) => qc.setQueryData(['habits'], ctx?.prev || []),
    onSettled: () => qc.invalidateQueries({ queryKey: ['habits'] }),
  });
}

export function useDeleteHabit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => db.deleteHabit(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['habits'] });
      const prev = qc.getQueryData(['habits']) || [];
      qc.setQueryData(['habits'], prev.filter(h => h.id !== id));
      // Drop local checkins for this habit too.
      const prevCheckins = qc.getQueryData(['checkins']) || [];
      qc.setQueryData(['checkins'], prevCheckins.filter(c => c.habitId !== id));
      return { prev, prevCheckins };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(['habits'], ctx.prev);
      if (ctx?.prevCheckins) qc.setQueryData(['checkins'], ctx.prevCheckins);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['habits'] });
      qc.invalidateQueries({ queryKey: ['checkins'] });
    },
  });
}

// ───────────── CHECKINS ─────────────
export function useCheckins() {
  return useQuery({
    queryKey: ['checkins'],
    queryFn: () => db.listCheckins(),
  });
}

export function useToggleCheckin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ habitId, isoDate, next }) => {
      if (next) await db.setCheckin(habitId, isoDate, 'done');
      else      await db.clearCheckin(habitId, isoDate);
    },
    onMutate: async ({ habitId, isoDate, next }) => {
      await qc.cancelQueries({ queryKey: ['checkins'] });
      const prev = qc.getQueryData(['checkins']) || [];
      const existing = prev.find(c => c.habitId === habitId && c.date === isoDate);
      let nextList;
      if (next) {
        nextList = existing
          ? prev.map(c => c.habitId === habitId && c.date === isoDate ? { ...c, status: 'done' } : c)
          : [...prev, { habitId, date: isoDate, status: 'done' }];
      } else {
        nextList = prev.filter(c => !(c.habitId === habitId && c.date === isoDate));
      }
      qc.setQueryData(['checkins'], nextList);
      return { prev };
    },
    onError: (_e, _v, ctx) => qc.setQueryData(['checkins'], ctx?.prev || []),
    // Don't invalidate on settle — realtime channel handles eventual consistency.
  });
}

// ───────────── GOALS ─────────────
export function useGoals() {
  return useQuery({ queryKey: ['goals'], queryFn: () => db.listGoals() });
}

export function useCreateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => db.createGoal(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: ['goals'] });
      const prev = qc.getQueryData(['goals']) || [];
      qc.setQueryData(['goals'], [...prev, {
        id: `tmp-${Date.now()}`, kind: input.kind, period: input.period,
        text: input.text, checked: false, sortOrder: prev.length,
      }]);
      return { prev };
    },
    onError: (_e, _v, ctx) => qc.setQueryData(['goals'], ctx?.prev || []),
    onSettled: () => qc.invalidateQueries({ queryKey: ['goals'] }),
  });
}

export function useUpdateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => db.updateGoal(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ['goals'] });
      const prev = qc.getQueryData(['goals']) || [];
      qc.setQueryData(['goals'], prev.map(g => g.id === id ? { ...g, ...patch } : g));
      return { prev };
    },
    onError: (_e, _v, ctx) => qc.setQueryData(['goals'], ctx?.prev || []),
    onSettled: () => qc.invalidateQueries({ queryKey: ['goals'] }),
  });
}

// Hard delete a goal. Optimistic — pulls the row from the cache up front so
// the UI snaps shut, and rolls back if the network call fails.
export function useDeleteGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => db.deleteGoal(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['goals'] });
      const prev = qc.getQueryData(['goals']) || [];
      qc.setQueryData(['goals'], prev.filter(g => g.id !== id));
      return { prev };
    },
    onError: (_e, _v, ctx) => qc.setQueryData(['goals'], ctx?.prev || []),
    onSettled: () => qc.invalidateQueries({ queryKey: ['goals'] }),
  });
}

// ───────────── PROFILE ─────────────
export function useProfile() {
  return useQuery({ queryKey: ['profile'], queryFn: () => db.getProfile() });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch) => db.updateProfile(patch),
    onSuccess: (data) => qc.setQueryData(['profile'], data),
  });
}

// ───────────── BRIEFING ─────────────
export function useBriefing(isoDate = todayIsoKey()) {
  return useQuery({
    queryKey: ['briefing', isoDate],
    queryFn: () => db.getBriefing(isoDate),
  });
}

export function useUpsertBriefing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ isoDate, data }) => db.upsertBriefing(isoDate, data),
    onMutate: async ({ isoDate, data }) => {
      await qc.cancelQueries({ queryKey: ['briefing', isoDate] });
      const prev = qc.getQueryData(['briefing', isoDate]);
      qc.setQueryData(['briefing', isoDate], {
        tasks: data.tasks || [],
        focus: data.focus || null,
        calls: data.calls || [],
        date_display: data.date_display || null,
      });
      return { prev };
    },
    onError: (_e, { isoDate }, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(['briefing', isoDate], ctx.prev);
    },
    onSettled: (_d, _e, { isoDate }) => qc.invalidateQueries({ queryKey: ['briefing', isoDate] }),
  });
}

export function useUpdateBriefingTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => db.updateBriefingTask(id, patch),
    onMutate: async ({ id, patch, isoDate }) => {
      await qc.cancelQueries({ queryKey: ['briefing', isoDate] });
      const prev = qc.getQueryData(['briefing', isoDate]);
      if (prev) {
        qc.setQueryData(['briefing', isoDate], {
          ...prev,
          tasks: prev.tasks.map(t => t.id === id ? { ...t, ...patch } : t),
        });
      }
      return { prev };
    },
    onError: (_e, { isoDate }, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(['briefing', isoDate], ctx.prev);
    },
  });
}

export function useDeleteBriefingTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => db.deleteBriefingTask(id),
    onMutate: async ({ id, isoDate }) => {
      await qc.cancelQueries({ queryKey: ['briefing', isoDate] });
      const prev = qc.getQueryData(['briefing', isoDate]);
      if (prev) {
        qc.setQueryData(['briefing', isoDate], {
          ...prev,
          tasks: prev.tasks.filter(t => t.id !== id),
        });
      }
      return { prev };
    },
    onError: (_e, { isoDate }, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(['briefing', isoDate], ctx.prev);
    },
  });
}
